import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Configure axios defaults
axios.defaults.withCredentials = true;

// Global 401 interceptor — if any request comes back with 401 we try to silently
// refresh the access token (via the HttpOnly refresh cookie) and retry the
// request ONCE. Prevents mid-form 401s from kicking a user out while they were
// typing. The marker prevents infinite loops on the refresh endpoint itself.
let _refreshInFlight = null;
axios.interceptors.response.use(
    (resp) => resp,
    async (error) => {
        const original = error.config || {};
        const status = error.response?.status;
        const url = original.url || '';
        if (status !== 401 || original._retried || url.includes('/api/auth/refresh') || url.includes('/api/auth/login')) {
            return Promise.reject(error);
        }
        original._retried = true;
        try {
            if (!_refreshInFlight) {
                _refreshInFlight = axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
            }
            await _refreshInFlight;
            _refreshInFlight = null;
            return axios(original);
        } catch (e) {
            _refreshInFlight = null;
            return Promise.reject(error);
        }
    }
);

// Format API error details
function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
    const [loading, setLoading] = useState(true);

    // Normalize the user object so `user.id` is always present regardless of whether
    // the backend returned `_id` (from /auth/me) or `id` (from /auth/login).
    const normalize = (u) => {
        if (!u || typeof u !== 'object') return u;
        return { ...u, id: u.id || u._id };
    };

    const setUserNormalized = (u) => setUser(u === false || u === null ? u : normalize(u));

    const checkAuth = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
            setUserNormalized(data);
        } catch (error) {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    // Silent session refresh — every ~55 min, call /auth/refresh so the 60-min
    // access token never times out while a tab is open. The refresh token
    // (HttpOnly cookie, 7d or 30d) is sent automatically with the request.
    // Also runs when the tab regains focus after a long idle period, so a user
    // coming back from lunch doesn't need to log in again.
    useEffect(() => {
        if (!user) return;
        let cancelled = false;
        const doRefresh = async () => {
            try {
                await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
            } catch {
                // 401 → refresh token expired. Let the next /auth/me re-check.
            }
        };
        const timer = setInterval(() => { if (!cancelled) doRefresh(); }, 55 * 60 * 1000);
        const onVisible = () => { if (!document.hidden && !cancelled) doRefresh(); };
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            cancelled = true;
            clearInterval(timer);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [user]);

    const login = async (email, password, remember = false) => {
        try {
            const { data } = await axios.post(
                `${API_URL}/api/auth/login`,
                { email, password, remember },
                { withCredentials: true }
            );
            setUserNormalized(data);
            return { success: true };
        } catch (error) {
            return { 
                success: false, 
                error: formatApiErrorDetail(error.response?.data?.detail) || error.message 
            };
        }
    };

    const register = async (email, password, name) => {
        try {
            const { data } = await axios.post(
                `${API_URL}/api/auth/register`,
                { email, password, name },
                { withCredentials: true }
            );
            setUserNormalized(data);
            return { success: true };
        } catch (error) {
            return { 
                success: false, 
                error: formatApiErrorDetail(error.response?.data?.detail) || error.message 
            };
        }
    };

    const logout = async () => {
        try {
            await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            setUser(false);
        }
    };

    const refreshUser = async () => {
        await checkAuth();
    };

    const value = {
        user,
        loading,
        login,
        register,
        logout,
        refreshUser,
        isAuthenticated: !!user && user !== false
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
